import { ArrowDownward, ArrowUpward, FileCopy } from "@mui/icons-material";
import { Breadcrumbs, IconButton, Link, TextField, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { ExecutionService } from "../../services/ExecutionService";
import { Execution } from "../../types/Execution";
import { useUtils } from "../../utils/utilFunctions";
import { getColor, getLoading, getStatus } from "../execution-events/ExecutionEvents";
import './Executions.scss';

const Executions: FC = () => {
	const [executions, setExecutions] = useState<Execution[]>([]);
	const [filteredExecutions, setFilteredExecutions] = useState<Execution[]>([]);
	const [search, setSearch] = useState('');
	const [fieldOrder, setFieldOrder] = useState<'NAME' | 'TOTAL' | 'DATE' | 'STATUS'>('DATE');
	const [ordemAsc, setOrdemAsc] = useState(false);
	const [loading, setLoading] = useState(true);
	const [permissions] = useAtom(permissionsAtom);
	const executionService = new ExecutionService();
	const navigate = useNavigate();
	const { verifyPermission, handleCopy, parseDate } = useUtils();

	const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearch(event.target.value);
	}

	const goToComparations = (id: number) => {
		navigate(`/execucoes/${id}/comparacoes`);
	}

	function mudaOrdem(field: 'NAME' | 'TOTAL' | 'DATE' | 'STATUS') {
		setFieldOrder(field);
		setOrdemAsc(!ordemAsc);
	}

	useEffect(() => {
		const loadExecutions = async () => {
			const executions = await executionService.getExecutions();
			if (executions) {
				setExecutions(executions.data);
				setFilteredExecutions(executions.data);
			}
			setLoading(false);
		};
		loadExecutions();
	}, []);

	useEffect(() => {
		if (!search) {
			setFilteredExecutions(executions);
		}
		setFilteredExecutions(executions.filter(item =>
			item.companyName.toUpperCase().includes(search.toUpperCase())));
	}, [search]);

	useEffect(() => {
		if (!!permissions.length) {
			verifyPermission(permissions, 'EXECUTIONS');
		}
	}, []);

	return (
		<div className="extracoes">
			<AppBar
				modulo="Execuções"
			/>
			<div className="component">
				<div className="top">
					<div className="etapas">
						<Breadcrumbs aria-label="breadcrumb">
							<Link variant="h6" underline="hover" color="inherit" href="/execucoes">
								Execuções
							</Link>
						</Breadcrumbs>
					</div>
					<div className="search">
						<TextField
							sx={{width: '300px'}}
							label="Buscar empresa pelo nome"
							id="company-search"
							size="small"
							value={search}
							onChange={handleChangeSearch}
						/>
					</div>
				</div>
				{!loading && filteredExecutions && filteredExecutions.length
					? <div className="tabela">
					<div className="header-tabela">
						<p onClick={() => mudaOrdem('NAME')} id="sort">Empresa {obtemIconeOrdem('NAME', fieldOrder, ordemAsc)}</p>
						<p>CNPJ matriz</p>
						<p onClick={() => mudaOrdem('TOTAL')} id="sort-x">Total extraído {obtemIconeOrdem('TOTAL', fieldOrder, ordemAsc)}</p>
						<p onClick={() => mudaOrdem('DATE')} id="sort-x">Última execução {obtemIconeOrdem('DATE', fieldOrder, ordemAsc)}</p>
						<p onClick={() => mudaOrdem('STATUS')} id="sort-x">Status {obtemIconeOrdem('DATE', fieldOrder, ordemAsc)}</p>
					</div>
					<div className="body-table">
						{filteredExecutions.sort((a, b) => {
							let valueA: string | number;
							let valueB: string | number;
							switch (fieldOrder) {
								case 'TOTAL':
									valueA = a.totalProcessed;
									valueB = b.totalProcessed;
									break;
								case 'DATE':
									valueA = parseDate(a.executionDate).getTime();
									valueB = parseDate(b.executionDate).getTime();
									break;
								case 'NAME':
									valueA = a.companyName;
									valueB = b.companyName;
									break;
								default:
									valueA = Number(a.hasDivergence);
									valueB = Number(b.hasDivergence);
									break;
							}
							if (typeof valueA === 'string' && typeof valueB === 'string') {
								return ordemAsc
									? valueA.localeCompare(valueB)
									: valueB.localeCompare(valueA);
							} else {
								return ordemAsc
									? (valueA as number) - (valueB as number)
									: (valueB as number) - (valueA as number);
							}
						}).map((execution) => (
							<div id={execution.id + ''} className="linha-empresa">
								<p onClick={() => goToComparations(execution.id)} id="sort">{execution.companyName}</p>
								<p
									style={{cursor: "default"}}
								>
									{execution.cnpj}
									<IconButton onClick={() => handleCopy(execution.cnpj)}>
										<FileCopy />
									</IconButton>
								</p>
								<p onClick={() => goToComparations(execution.id)} className={getColor(execution.totalProcessed)}>
									{getTotalExecuted(execution.totalProcessed)}
								</p>
								<p onClick={() => goToComparations(execution.id)}>
									{execution.executionDate}
								</p>
								{getStatus(execution.hasDivergence)}
							</div>
						))}
					</div>
				</div>
				: !loading ? <div className="sem-execucoes">
					<Typography variant="h5">
						Nenhuma execução realizada por enquanto...
					</Typography>
				</div> : <></>}
				{loading
					? getLoading()
					: <></>
				}
			</div>
		</div>
	);

	function getTotalExecuted(value: string) {
		return value === "0/0" ? "Nenhum funcionário ativo" : value;
	}
}

export function obtemIconeOrdem(field: string, fieldOrder: string, ordemAsc: boolean) {
	return field === fieldOrder
		? ordemAsc
			? <ArrowUpward sx={{marginLeft: "338px"}}/>
			: <ArrowDownward sx={{marginLeft: "338px"}}/>
		: <ArrowDownward sx={{marginLeft: "338px"}}/>;
}

export default Executions;